<template>
    <form @submit.prevent="signIn" class="login">
        <template v-if="!showCodeConfirmation">
            <div class="field">
                <div class="control">
                    <masked-phone v-if="client && client.login.phoneMask"
                                  v-model="login"
                                  :mask="client.login.phoneMask"
                                  v-validate="{ required: true, length: (client.login.phoneMask.match(/\d/g) || []).length }"
                                  data-vv-name="login"
                                  data-vv-validate-on="input"
                                  :class="['input', 'is-small', {'is-danger': errors.has('login')}]"
                                  autocomplete="username"
                                  @focus="clearServerErrors()" />
                    <input v-else
                           v-model="login"
                           v-validate="`required|min:3|max:50`"
                           data-vv-name="login"
                           data-vv-validate-on="change"
                           @focus="clearServerErrors()"
                           :class="{'is-danger': errors.has('login')}"
                           class="input is-small"
                           type="text"
                           :placeholder="'components.common.quick-login-reg.login.login' | translate"
                           autofocus
                           maxlength="50"
                           autocomplete="username" />
                </div>
                <p class="help is-danger" v-if="errors.has('login:required')">{{"components.common.quick-login-reg.login.validation.login required" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('login:min')">{{ $t("components.common.quick-login-reg.login.validation.login min length", $validator.fields.find({ name: 'login' }).rules) }}</p>
                <p class="help is-danger" v-if="errors.has('login:max')">{{ $t("components.common.quick-login-reg.login.validation.login max length", $validator.fields.find({ name: 'login' }).rules) }}</p>
            </div>
            <div class="field">
                <div class="control has-icons-right">
                    <input v-model="password"
                           v-validate="`required|min:4|max:20`"
                           data-vv-name="password"
                           data-vv-validate-on="change"
                           @focus="clearServerErrors()"
                           :class="{'is-danger': errors.has('password')}"
                           class="input is-small"
                           :type="showPassword ? 'text' : 'password'"
                           :placeholder="'components.common.quick-login-reg.login.password' | translate"
                           maxlength="20"
                           autocomplete="password"
                           ref="password" />
                    <a class="icon is-small is-right is-clickable" @click="toggleShowPassword()">
                        <span v-show="!showPassword"><i class="far fa-eye-slash"></i></span>
                        <span v-show="showPassword"><i class="far fa-eye"></i></span>
                    </a>
                </div>
                <p class="help is-danger" v-if="errors.has('password:required')">{{"components.common.quick-login-reg.login.validation.password required" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('password:min')">{{"components.common.quick-login-reg.login.validation.password min length" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('password:max')">{{"components.common.quick-login-reg.login.validation.password max length" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('login:incorrectLoginOrPassword', 'serverErrors')">{{"components.common.quick-login-reg.login.validation.incorrect login or password" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('login:playerIsLocked', 'serverErrors') && lockTime <= 60">{{$t("components.common.quick-login-reg.login.validation.player is locked", { time: lockTime })}}</p>
                <p class="help is-danger" v-if="errors.has('login:playerIsLocked', 'serverErrors') && lockTime > 60">{{$t("components.common.quick-login-reg.login.validation.player is locked for a long time")}}</p>
                <p class="help is-danger has-text-weight-bold" v-if="failedAccessAttemptsLeft">{{"components.common.quick-login-reg.login.validation.failed access warning" | translate}} {{failedAccessAttemptsLeft}}</p>
            </div>
        </template>
        <email-confirmation v-if="showCodeConfirmation" :value.sync="code" />
        <recaptcha v-show="false" ref="recaptcha" :on-response="response => { recaptchaResponse = response; }" />
        <div class="field">
            <button class="button is-small is-warning play-button" :disabled="errors.any() || loading || !isRecaptchaVerified">
                <span v-if="!loading">{{ $t(showCodeConfirmation ? "common.confirm" : "components.common.quick-login-reg.login.log in") }}</span>
                <span class="icon" v-if="loading"><i class="fas fa-circle-notch fa-pulse"></i></span>
            </button>
            <button v-if="showCodeConfirmation && !loading" class="button is-small" @click.prevent="() => { showCodeConfirmation = false; code = null; errors.clear('serverErrors'); }" :disabled="loading">
                <span>{{"common.cancel" | translate}}</span>
            </button>
        </div>
        <div v-if="passwordRestoreAllowed" class="field">
            <router-link :to="{name: 'password-restore'}" class="has-text-link is-size-7">{{"components.common.quick-login-reg.login.restore password" | translate}}</router-link>
        </div>
    </form>
</template>

<script>
    import Vue from 'vue';

    import Recaptcha from '../recaptcha/recaptcha.vue';
    import MaskedPhone from '../phone-number/phone-number-masked.vue';
    import EmailConfirmation from "./email-confirmation.vue";

    import authService from '../../../../../_common/services/authentication.service';

    export default {
        name: 'quick-login-reg-login',
        $_veeValidate: { validator: 'new' },
        components: { Recaptcha, MaskedPhone, EmailConfirmation },
        data() {
            return {
                showPassword: false,
                loading: false,
                login: '',
                password: '',
                recaptchaResponse: null,
                failedAccessAttemptsLeft: null,
                lockTime: null,
                code: null,
                showCodeConfirmation: false
            };
        },
        computed: {
            lang: {
                get() {
                    return Vue.state.lang.currentLang;
                },
                set(val) {
                    Vue.state.lang.currentLang = val;
                }
            },
            client() {
                return Vue.state.client.get();
            },
            passwordRestoreAllowed() {
                return this.client && this.client.view.auth.passwordRestoreAllowed;
            },
            isRecaptchaVerified() {
                return !this.$refs.recaptcha || this.$refs.recaptcha.verified;
            }
        },
        methods: {
            hide() {
                this.resetFields();
            },
            resetFields() {
                this.login = '';
                this.password = '';
                this.failedAccessAttemptsLeft = null;
            },
            async signIn() {

                await this.$validator.validateAll();

                if (this.errors.any() || this.loading || !this.isRecaptchaVerified || !this.login || !this.password) {
                    return;
                }

                this.loading = true;
                this.errors.clear();
                this.errors.clear("serverErrors");

                const loginResult = await authService.login({
                    login: this.$data.login,
                    password: this.$data.password,
                    recaptchaResponse: this.recaptchaResponse,
                    code: this.code
                });

                this.failedAccessAttemptsLeft = loginResult.failedAccessAttemptsLeft;

                if (loginResult.lockTime) {
                    this.lockTime = parseInt(Math.ceil(loginResult.lockTime / 60000));
                }

                if (loginResult.isSuccess) {
                    if (loginResult.codeSent) {
                        this.showCodeConfirmation = true;
                    }
                    else {
                        await this.play();
                    }
                } else {
                    this.errors.add({ field: 'login', rule: loginResult.error, msg: loginResult.error, scope: 'serverErrors' });
                    this.errors.add({ field: 'password', rule: loginResult.error, msg: loginResult.error, scope: 'serverErrors' });
                    this.errors.add({ field: 'confirmation code', rule: loginResult.error, msg: loginResult.error, scope: 'serverErrors' });
                }
                this.loading = false;
            },
            clearServerErrors() {
                this.errors.remove('login', 'serverErrors');
                this.errors.remove('password', 'serverErrors');
                this.errors.remove('confirmation code', 'serverErrors');
            },
            async play() {
                await Vue.state.player.reload();
                Vue.state.lang.ensureLocalLangOrPlayer(Vue.state.player.get().lang || 'ru');
                await Vue.router.teleport.home();
            },
            toggleShowPassword() {
                this.showPassword = !this.showPassword;
                setTimeout(() => this.$refs.password.focus());
            }
        },
        beforeDestroy() {
            this.resetFields();
        }
    };
</script>

<style lang="scss">
</style>


// WEBPACK FOOTER //
// front/src/skins/bulmasite/components/common/quick-login-reg/login.vue